import {Subset} from '@wandb/weave/common/types/base';
import {addDays} from '@wandb/weave/common/util/time';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Link, useHistory} from 'react-router-dom';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button, Header, Modal} from 'semantic-ui-react';

import {
  useClaimAnonymousEntityMutation,
  UserAccountType,
} from '../generated/graphql';
import {auth} from '../setup';
import {User} from '../types/graphql';
import {safeLocalStorage} from '../util/localStorage';
import {entity, run as runURL} from '../util/urls';
import {AnonymousEntityClaim, ClaimTask} from './AnonymousEntityClaimer';
import * as S from './AnonymousNudgeBar';

const AUTO_CLAIM_STORE_KEY = '_WB_CLAIM_ANONYMOUS_RUN';
const ANON_EXPIRY_ADDED_DATE = new Date('2021-11-22');

interface AnonymousClaimBannerProps {
  entityName: string;
  projectName: string;
  run: string;
  user: Subset<User, 'id' | 'username' | 'apiKey' | 'accountType'> | null;
  viewer: User | null;
  createdAt: string;
}

const AnonymousClaimBanner: React.FC<AnonymousClaimBannerProps> = React.memo(
  ({entityName, projectName, run, user, viewer, createdAt}) => {
    const [claimModalOpen, setClaimModalOpen] = useState(false);
    const [claim, setClaim] = useState<ClaimTask | undefined>(undefined);
    const history = useHistory();

    // anonymous runs expire after 7 days
    const expiresOn = addDays(new Date(createdAt), 7);
    const dateNow = new Date();
    const diff = expiresOn.getTime() - dateNow.getTime();
    const daysLeft = Math.floor(diff / (1000 * 3600 * 24));

    // For now, only runs that were created after 2021-11-22 will expire
    const willExpire = new Date(createdAt) > ANON_EXPIRY_ADDED_DATE;

    const [claimAnonymousEntity] = useClaimAnonymousEntityMutation();

    const expiringRunMessage = useMemo(() => {
      return (
        <>
          {willExpire && daysLeft >= 0 && (
            <b>
              This unclaimed run expires in {daysLeft} day
              {daysLeft === 1 ? '' : 's'}.{' '}
            </b>
          )}
          {willExpire && daysLeft < 0 && (
            <b>This unclaimed run has expired and will be deleted soon! </b>
          )}
        </>
      );
    }, [willExpire, daysLeft]);

    const openClaimModal = useCallback(() => {
      setClaimModalOpen(true);
      window.location.hash = '#claim';
    }, []);

    const closeClaimModal = useCallback(() => {
      setClaimModalOpen(false);
      window.location.hash = '';
    }, []);

    const claimAnonEntity = useCallback(
      async (key: string) => {
        const res = await claimAnonymousEntity({
          variables: {
            anonymousApiKey: key,
          },
        });

        if (!res.data?.claimAnonymousEntity) {
          return;
        }

        setClaim(res.data.claimAnonymousEntity.task);
      },
      [claimAnonymousEntity]
    );

    const apiKey = user?.apiKey;

    useEffect(() => {
      if (
        !apiKey ||
        safeLocalStorage.getItem(AUTO_CLAIM_STORE_KEY) !== apiKey
      ) {
        return;
      }

      safeLocalStorage.removeItem(AUTO_CLAIM_STORE_KEY);

      claimAnonEntity(apiKey);
    }, [apiKey, claimAnonEntity]);

    if (user === null) {
      return <div />;
    }

    const {accountType} = user;

    // Users are only claimable if they are anonymous and the current viewer
    // has access to their API key, which means the user is supplying the
    // anonymous user's API key in the request header.
    if (accountType !== UserAccountType.Anonymous || apiKey == null) {
      return <div />;
    }

    // The user is claimable, but we're not logged in. Prompt the user to log in.
    if (!viewer) {
      return (
        <S.AnonymousNudgeBar>
          <S.AnonymousNudgeBarText>
            <S.AnonymousNudgeBarTitle>
              {expiringRunMessage}
              Sign in to a free account to save your experiment.
            </S.AnonymousNudgeBarTitle>
          </S.AnonymousNudgeBarText>
          <S.AnonymousNudgeBarButton>
            <S.AnonymousNudgeBarActionLink
              onClick={() => {
                window.analytics?.track(
                  'Sign In to Claim Anonymous Run Clicked',
                  {
                    entityName,
                    projectName,
                    runName: run,
                  }
                );
                safeLocalStorage.setItem(AUTO_CLAIM_STORE_KEY, apiKey);
                auth.login();
              }}>
              Save my data
            </S.AnonymousNudgeBarActionLink>
          </S.AnonymousNudgeBarButton>
        </S.AnonymousNudgeBar>
      );
    }

    const claimInProgress = claim !== undefined;

    // User is claimable and we're logged in. In addition to the banner,
    // also pop up a modal that guides the user through the claim workflow.
    return (
      <React.Fragment>
        <S.AnonymousNudgeBar>
          <S.AnonymousNudgeBarText>
            <S.AnonymousNudgeBarTitle>
              {expiringRunMessage}
              Claim this run to save it to your account.
            </S.AnonymousNudgeBarTitle>
          </S.AnonymousNudgeBarText>
          <S.AnonymousNudgeBarButton>
            <S.AnonymousNudgeBarActionLink onClick={openClaimModal}>
              Save my data
            </S.AnonymousNudgeBarActionLink>
          </S.AnonymousNudgeBarButton>
        </S.AnonymousNudgeBar>
        <Modal open={claimModalOpen}>
          <Header>
            Would you like to claim anonymous user{' '}
            <Link to={entity(entityName)}>{entityName}</Link>?
          </Header>
          <Modal.Content>
            <p>
              All of its projects and runs will be transferred to your account.
            </p>
            {claimInProgress && (
              <AnonymousEntityClaim
                task={claim!}
                onFinished={() => {
                  closeClaimModal();

                  // Once the claim is complete, the run has been moved
                  // into the viewer's entity. Redirect to that run.
                  history.push(
                    runURL({
                      entityName: viewer.userEntity.name,
                      projectName,
                      name: run,
                    })
                  );
                }}
                onError={() => {
                  setClaimModalOpen(false);
                }}
              />
            )}
          </Modal.Content>
          <Modal.Actions>
            <Button
              visible={!claimInProgress}
              negative
              onClick={closeClaimModal}>
              Leave unclaimed
            </Button>
            <Button
              positive
              onClick={() => {
                window.analytics?.track('Claim Anonymous Run Clicked', {
                  entityName,
                  projectName,
                  runName: run,
                });
                claimAnonEntity(apiKey);
              }}
              disabled={claimInProgress}
              loading={claimInProgress}>
              Claim
            </Button>
          </Modal.Actions>
        </Modal>
      </React.Fragment>
    );
  }
);

export default AnonymousClaimBanner;
